import React from 'react';

import { classnames, useClassnames } from '@lumapps/classnames';
import { useSortable } from '@lumapps/lumx-dnd/hooks';
import type { UniqueIdentifier } from '@lumapps/lumx-dnd/types';
import { CSS } from '@lumapps/lumx-dnd/utils';
import { TableCell, TableRow } from '@lumapps/lumx/react';

import { SortableListProps } from '../types';
import './index.scss';

const CLASSNAME = 'lumx-sortable-list-item';

export type ItemRef = HTMLLIElement | HTMLTableRowElement | null;

export interface SortableListItemProps<T>
    extends Pick<
            SortableListProps<T>,
            | 'gap'
            | 'withDragHandle'
            | 'withDirectMovement'
            | 'entityRenderer'
            | 'className'
            | 'columns'
            | 'onFocus'
            | 'onEnter'
        >,
        Pick<Required<SortableListProps<T>>, 'mode'> {
    entity: T;
    id: UniqueIdentifier;
    index: number;
    isDragged: boolean;
    isDisabled?: boolean;
    itemRef?: (el: ItemRef) => void;
}

export const SortableListItem = <T,>({
    className,
    columns,
    entity,
    id,
    index,
    isDragged,
    gap,
    mode,
    withDragHandle = false,
    withDirectMovement = true,
    isDisabled = false,
    entityRenderer,
    itemRef,
    onEnter,
    onFocus,
}: SortableListItemProps<T>) => {
    const { block, element } = useClassnames(CLASSNAME);
    const { block: customBlock } = useClassnames(`${className || CLASSNAME}-item`);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isSorting,
        transition,
        isDragging,
        activeIndex,
        active,
        over,
        setActivatorNodeRef,
    } = useSortable({
        id,
        disabled: isDisabled,
    });

    const isPlaceholder = isDragging && !isDragged;

    const insertPosition = React.useMemo(() => {
        if (over?.id === id && activeIndex !== null) {
            return index > activeIndex ? 'after' : 'before';
        }
        return undefined;
    }, [over?.id, id, activeIndex, index]);

    const style = {
        transform: isSorting && !withDirectMovement ? undefined : CSS.Translate.toString(transform),
        transition,
    };

    const indicatorElement = insertPosition && !isDragged && !withDirectMovement && over?.id !== active?.id && (
        <div
            className={element('insert-indicator', { [insertPosition]: true, [mode]: true })}
            style={
                {
                    // eslint-disable-next-line lumapps/no-ds-classes
                    '--gap': gap ? `var(--lumx-spacing-unit-${gap})` : 0,
                } as React.CSSProperties
            }
        />
    );

    const ItemElement = mode === 'table' ? TableRow : 'li';

    return (
        <>
            {indicatorElement && insertPosition === 'before' && mode === 'table' ? (
                <TableRow>
                    <TableCell colspan={columns} className={element('insert-indicator-cell')}>
                        {indicatorElement}
                    </TableCell>
                </TableRow>
            ) : null}
            <ItemElement
                className={classnames(block({ [mode]: true }), customBlock({ [mode]: true }))}
                ref={(el: ItemRef) => {
                    setNodeRef(el);
                    itemRef?.(el);
                }}
                style={style}
                {...attributes}
                {...(!withDragHandle ? listeners : undefined)}
                tabIndex={!withDragHandle ? 0 : undefined}
                aria-roledescription={undefined}
                {...(withDragHandle ? { role: mode === 'table' ? 'row' : 'listitem' } : {})}
                // On focus
                onFocus={() => {
                    onFocus?.(index);
                }}
                onKeyDown={(event) => {
                    // Intercept Enter key to trigger onEnter callback
                    if (event.key === 'Enter' && onEnter) {
                        event.preventDefault();
                        onEnter?.(index);
                    }
                    // Forward event to dnd-kit
                    if (!withDragHandle) {
                        listeners?.onKeyDown?.(event);
                    }
                }}
            >
                {insertPosition === 'before' && mode !== 'table' && indicatorElement}
                {entityRenderer(entity, {
                    index,
                    isActive: isDragging,
                    isDragged,
                    isPlaceholder,
                    isDisabled,
                    isDragOngoing: isSorting,
                    wrapperProps: {
                        id: id.toString(),
                    },
                    dragHandleProps: withDragHandle
                        ? Object.assign(listeners || {}, {
                              ref: setActivatorNodeRef,
                              tabIndex: 0,
                              role: 'button',
                              'aria-labelledby': id,
                              'aria-describedby': attributes['aria-describedby'],
                              'aria-disabled': isDisabled || undefined,
                          })
                        : undefined,
                })}
                {insertPosition === 'after' && mode !== 'table' && indicatorElement}
            </ItemElement>
            {indicatorElement && insertPosition === 'after' && mode === 'table' ? (
                <TableRow>
                    <TableCell colspan={columns} className={element('insert-indicator-cell')}>
                        {indicatorElement}
                    </TableCell>
                </TableRow>
            ) : null}
        </>
    );
};
